import "amazon-connect-streams";
import { getLogger } from "@expert/logging";
import { immerable } from "immer";

import type { AgentActivity, ChatTask, TaskStatus } from "@/sdk";
import type { AsyncState, Partner } from "@/shared-types";

import { useChatStore } from "@/sdk";
import { DEFAULT_CHAT_WRAPPING_DURATION } from "@/sdk/agent/config";
import { isLocale } from "@/sdk/agent/types";

import type { AmazonConnectContactConfig } from "../AmazonConnectContact";

import AmazonConnectContact from "../AmazonConnectContact";
import normalizePartner from "../normalizePartner";
import contactStateTypeToTaskStatus from "../voice/contactStateTypeToTaskStatus";
import { getFallbackAssignedAtTime } from "./utils/contactStateUtils";
import { assertRequiredContactAttributes } from "./utils/typeGuards";

/**
 * Represents a chat task from Amazon Connect.
 * This class encapsulates the state and behavior of a single chat contact,
 * deriving its initial state from the raw `connect.Contact` object.
 */
export default class AmazonConnectChatContact extends AmazonConnectContact implements ChatTask {
    override [immerable] = true;
    static readonly logger = getLogger({ module: "AmazonConnectChatContact" });
    public readonly partner: Partner;
    public readonly hasCustomer = true;
    public readonly sourceApp: string;
    public readonly chatId: string;
    public readonly topic: string;
    public readonly language: string;
    /** Original chat ID from a transferred/continued chat session. */
    public readonly originalChatId: string | undefined;

    /** Amazon Connect chat tasks should always auto-accept */
    public override readonly autoAccept = true;
    public override readonly wrappingDuration = DEFAULT_CHAT_WRAPPING_DURATION;

    /** Customer's phone number. Encrypted, decrypted asynchronously. */
    mdn: AsyncState<string | undefined>;
    /** Customer's problem description. Encrypted, decrypted asynchronously. */
    problemStatement: AsyncState<string | undefined>;
    /** Customer's name. Encrypted, decrypted asynchronously. */
    customerName: AsyncState<string | undefined>;

    /**
     * Constructs a new AmazonConnectChatContact.
     *
     * The constructor is responsible for:
     * 1. Deriving the `locale` from contact attributes before calling `super()` to satisfy the parent constructor.
     * 2. Validating that all required attributes (`chatId`, `partner`, `sourceApp`) are present.
     * 3. Initializing all chat-specific properties from the contact attributes.
     */
    constructor(name: string, config: AmazonConnectContactConfig) {
        const { contact } = config;
        const contactAttributes = contact.getAttributes();
        const chatId = config.contact.getContactId();

        //#region - validation of required contact attributes before creating the contact instance
        if (!chatId) {
            throw new Error("FATAL: Amazon Connect contact is missing ID.");
        }
        assertRequiredContactAttributes(contactAttributes, chatId);
        //#endregion

        // After assertion, we know required attributes exist
        const language = contactAttributes.language.value;
        const locale = isLocale(language) ? language : undefined;

        // Derive needed values and validate before calling super() to satisfy the parent constructor
        super(name, config, locale);

        this.status = contactStateTypeToTaskStatus(contact.getState().type);
        // (used as agentJoined time for chat tasks) init with a fallback from contact state; updated with exact transcript agentJoinTime later
        this.assignedAt = getFallbackAssignedAtTime(contact);
        this.chatId = chatId;
        this.partner = normalizePartner(contactAttributes.partner.value)!;
        this.sourceApp = contactAttributes.sourceApp.value;
        this.topic = contactAttributes.topic.value;
        this.language = language;
        this.originalChatId = contactAttributes.originalChatId?.value;

        //#region - encrypted attributes - Init with loading: true
        this.mdn = { loading: true, value: undefined };
        this.problemStatement = { loading: true, value: undefined };
        this.customerName = { loading: true, value: undefined };
        //#endregion
    }

    /**
     * Encryption context for messages — `originalChatId` for transferred/continued chats, otherwise the
     * current contact id. Contact attributes use `contactAttributeEncryptionContext` in `syncContactAttributes`.
     */
    get messageEncryptionContext(): string {
        return this.originalChatId ?? this.chatId;
    }

    get isCustomerAbsent(): boolean {
        const autopilotState = useChatStore.getState().getChatAutopilot(this.chatId);
        return !!autopilotState?.startTime;
    }

    get customerAbsenceStartedAt(): number | undefined {
        return useChatStore.getState().getChatAutopilot(this.chatId)?.startTime;
    }

    get customerAbsenceEndsAt(): number | undefined {
        const autopilotState = useChatStore.getState().getChatAutopilot(this.chatId);

        return autopilotState?.startTime && autopilotState?.durationSeconds !== undefined
            ? autopilotState.startTime + autopilotState.durationSeconds * 1000
            : undefined;
    }

    /** Calculate the task status based on the current contact state */
    public calculateStatusFromContact(): TaskStatus {
        const contactState = this.config.contact.getState();
        return contactStateTypeToTaskStatus(contactState.type);
    }

    public override isAgentBusy(_agentActivity: AgentActivity): boolean {
        return ["assigned", "wrapping", "pending"].includes(this.status);
    }

    get isNeglected(): boolean {
        const neglectionState = useChatStore.getState().getChatNeglection(this.chatId);
        return !!neglectionState?.startTime;
    }

    get wasTaskNeglected(): boolean {
        return !!(this.neglectionCount && this.neglectionCount > 0);
    }

    get isClosedByCustomer(): boolean {
        return useChatStore.getState().getCloseReason(this.chatId) === "ClosedByCustomer";
    }
    get wasCustomerDisconnected(): boolean {
        return useChatStore.getState().getCloseReason(this.chatId) === "CustomerDisconnected";
    }

    get changeStateAt(): number | undefined {
        if (this.status === "wrapping") {
            const wrappingStart = this.config.contact.getState().timestamp;
            if (!wrappingStart) return undefined;

            return wrappingStart.getTime() + this.wrappingDuration;
        }

        if (this.isNeglected) {
            const neglectionState = useChatStore.getState().getChatNeglection(this.chatId);
            return neglectionState?.startTime && this.neglectionWarningDurationMillis !== undefined
                ? neglectionState.startTime + this.neglectionWarningDurationMillis
                : undefined;
        }

        if (this.isCustomerAbsent && this.customerAbsenceEndsAt) {
            return this.customerAbsenceEndsAt;
        }

        return undefined;
    }

    get stateLastChangedAt(): number | undefined {
        if (this.status === "wrapping") {
            return this.config.contact.getState().timestamp?.getTime();
        }

        if (this.isNeglected) {
            return useChatStore.getState().getChatNeglection(this.chatId)?.startTime;
        }

        if (this.isCustomerAbsent && this.customerAbsenceStartedAt) {
            return this.customerAbsenceStartedAt;
        }
        return undefined;
    }

    get neglectionWarningDurationMillis(): number | undefined {
        const neglectionState = useChatStore.getState().getChatNeglection(this.chatId);
        return neglectionState?.durationSeconds ? neglectionState.durationSeconds * 1000 : undefined;
    }

    get routingQueue(): string | undefined {
        const { contact } = this.config;
        const queueInfo = contact.getQueue();
        return queueInfo?.name;
    }

    get workerSid(): string {
        // TODO: Implement proper worker ID for Amazon Connect chat - may need agent ARN or connection ID
        AmazonConnectChatContact.logger.warn(
            "workerSid not implemented for Amazon Connect chat contact, returning contact ID for now",
        );
        return this.config.contact.getContactId();
    }

    get neglectionCount(): number | undefined {
        return useChatStore.getState().getChatNeglection(this.chatId)?.neglectionCount;
    }
}
